/**
 * Les bases de comparaison du dashboard consolidé, et les bornes qu'elles désignent.
 *
 * ## Pourquoi ce module existe
 *
 * Ces règles vivaient dans `ConsolidatedDashboardView`, où seul le tableau les appliquait.
 * Depuis que le sélecteur de période laisse choisir la base, deux écrans en ont besoin : celui
 * qui l'annonce et celui qui la calcule. Une seconde implémentation finirait par diverger, et
 * l'aperçu du sélecteur promettrait alors une période que le tableau n'irait pas chercher.
 *
 * ## Pourquoi pas `calculateComparisonDates` de `periodUtils`
 *
 * Elle existe, et elle est plus simple — elle décale les bornes d'un an, sans rien regarder
 * d'autre. C'est précisément ce qui ne convient pas ici : la S36 de 2026 va du lundi 31 août au
 * dimanche 6 septembre ; les mêmes dates en 2025 tombent du dimanche au samedi, à cheval sur
 * deux semaines. L'écart porterait alors sur des jours de semaine différents, et la
 * saisonnalité hebdomadaire — la première chose que ce tableau donne à lire — serait faussée.
 *
 * Les bornes sont en UTC de bout en bout, comme celles que le serveur rend.
 */

/** Les quatre bases, dans l'ordre où le sélecteur les propose. */
export const BASES_COMPARAISON = [
  { value: 'previous_period', label: 'Période précédente' },
  { value: 'previous_year', label: 'Année précédente' },
  { value: 'operation', label: 'Opération commerciale' },
  { value: 'custom', label: 'Personnalisée' }
];

/**
 * Les bases dont les bornes sont **désignées** et non calculées.
 *
 * Pour celles-là, les dates viennent de l'utilisateur — une plage saisie, ou celles d'une
 * opération — et `bornesDeComparaison` n'a rien à en dire.
 */
export const BASES_A_DATES_EXPLICITES = ['custom', 'operation'];

const enIso = (d) => d.toISOString().split('T')[0];

/** Numéro et année ISO d'une date : c'est le jeudi de la semaine qui les détermine. */
const semaineIso = (d) => {
  const jeudi = new Date(d);
  jeudi.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const premierJanvier = new Date(Date.UTC(jeudi.getUTCFullYear(), 0, 1));
  return {
    annee: jeudi.getUTCFullYear(),
    semaine: Math.ceil((((jeudi - premierJanvier) / 86400000) + 1) / 7)
  };
};

/** Lundi d'une semaine ISO donnée. Le 4 janvier appartient toujours à la semaine 1. */
const lundiDeLaSemaine = (anneeIso, semaine) => {
  const quatreJanvier = new Date(Date.UTC(anneeIso, 0, 4));
  const lundiDeLaSemaineUn = new Date(quatreJanvier);
  lundiDeLaSemaineUn.setUTCDate(quatreJanvier.getUTCDate() - ((quatreJanvier.getUTCDay() + 6) % 7));
  const lundi = new Date(lundiDeLaSemaineUn);
  lundi.setUTCDate(lundiDeLaSemaineUn.getUTCDate() + (semaine - 1) * 7);
  return lundi;
};

/**
 * La période de l'an dernier, **de même nature** — et non les mêmes dates décalées.
 *
 * Trois cas, du plus contraint au plus général :
 *
 * - **une semaine ISO** se compare à la même semaine ISO de l'année précédente, lundi à
 *   lundi. La semaine 53 n'existe pas tous les ans : on retombe alors sur la 52 ;
 * - **un mois entier** se compare au même mois, du 1er au dernier jour — qui n'a pas
 *   forcément le même quantième (février) ;
 * - **tout le reste** garde ses bornes, décalées d'un an.
 */
const anneePrecedente = (d, f) => {
  const joursCouverts = Math.round((f - d) / 86400000) + 1;

  if (joursCouverts === 7 && d.getUTCDay() === 1) {
    const { annee, semaine } = semaineIso(d);
    let lundi = lundiDeLaSemaine(annee - 1, semaine);
    // Une année de 52 semaines renverrait la 53 sur la semaine 1 de la suivante
    if (semaineIso(lundi).semaine !== semaine) lundi = lundiDeLaSemaine(annee - 1, semaine - 1);
    const dimanche = new Date(lundi);
    dimanche.setUTCDate(lundi.getUTCDate() + 6);
    return { debut: enIso(lundi), fin: enIso(dimanche) };
  }

  const dernierJourDuMois = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  const moisEntier = d.getUTCFullYear() === f.getUTCFullYear()
    && d.getUTCMonth() === f.getUTCMonth()
    && d.getUTCDate() === 1
    && f.getUTCDate() === dernierJourDuMois;

  if (moisEntier) {
    const annee = d.getUTCFullYear() - 1;
    const mois = d.getUTCMonth();
    return {
      debut: enIso(new Date(Date.UTC(annee, mois, 1))),
      fin: enIso(new Date(Date.UTC(annee, mois + 1, 0)))
    };
  }

  // Cas général : mêmes quantièmes, un an plus tôt. Le 29 février revient au 28.
  const memeJour = (x) => {
    const annee = x.getUTCFullYear() - 1;
    const mois = x.getUTCMonth();
    const dernier = new Date(Date.UTC(annee, mois + 1, 0)).getUTCDate();
    return enIso(new Date(Date.UTC(annee, mois, Math.min(x.getUTCDate(), dernier))));
  };

  return { debut: memeJour(d), fin: memeJour(f) };
};

/**
 * La période de même durée qui précède immédiatement celle-ci.
 *
 * Aucune reconnaissance de calendrier ici, et c'est voulu : « la période précédente » de sept
 * jours, ce sont les sept jours d'avant, quels qu'ils soient. Celui qui veut la semaine civile
 * précédente choisit sa période dans le calendrier, et la reconnaissance joue alors sur
 * « année précédente ».
 */
const periodePrecedente = (d, f) => {
  const joursCouverts = Math.round((f - d) / 86400000) + 1;
  const finPrecedente = new Date(d);
  finPrecedente.setUTCDate(d.getUTCDate() - 1);
  const debutPrecedent = new Date(finPrecedente);
  debutPrecedent.setUTCDate(finPrecedente.getUTCDate() - joursCouverts + 1);
  return { debut: enIso(debutPrecedent), fin: enIso(finPrecedente) };
};

/**
 * Les bornes de référence pour une période et une base données.
 *
 * @param {string} debut 'YYYY-MM-DD' de la période regardée
 * @param {string} fin   'YYYY-MM-DD' de la période regardée
 * @param {string} base  une valeur de `BASES_COMPARAISON`. Par défaut la période précédente :
 *   sept jours regardés se comparent aux sept jours d'avant, trente aux trente d'avant. C'est
 *   la lecture qu'on attend d'un tableau qu'on vient d'ouvrir ; l'année précédente reste à un
 *   clic, et garde alors la durée de la période — trente jours contre trente jours, l'an passé.
 * @returns {{debut: string, fin: string}|null} `null` quand la base porte ses propres dates
 *   (`custom`, `operation`) ou que les bornes reçues ne sont pas exploitables.
 */
export const bornesDeComparaison = (debut, fin, base = 'previous_period') => {
  if (BASES_A_DATES_EXPLICITES.includes(base)) return null;
  if (!debut || !fin) return null;

  const d = new Date(`${debut}T00:00:00Z`);
  const f = new Date(`${fin}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime()) || f < d) return null;

  return base === 'previous_period' ? periodePrecedente(d, f) : anneePrecedente(d, f);
};
